import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import {
  Button,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Typography,
} from 'antd';
import { v4 } from 'uuid';
import Table, { ColumnsType } from 'antd/lib/table';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import { FieldData } from '@/types/FieldData';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  CompaniesInfiniteSelectField,
  DeleteIcon,
  InputDecimal,
} from '@/components/_common';
import { quantityFormatter } from '@/utils/number';
import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import StatusTag from '@/components/_common/StatusTag';
import { StockCorrectionFormValues } from '@/core/domain/stock/StockCorrections/StockCorrections.types';
import { getTableScrollX } from '@/utils/table';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import StockCorrectionCreateModal from '../StockCorrectionCreateModal';

const { Title, Text } = Typography;

const getRowKey = (item: StockItem) =>
  JSON.stringify({
    partId: item.partId,
    consumableId: item.consumableId,
  });

interface StockCorrectionFormProps {
  form: FormInstance<StockCorrectionFormValues>;
}

export const StockCorrectionForm: React.FC<StockCorrectionFormProps> = ({
  form,
}: StockCorrectionFormProps) => {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const user = getUserFromLocalStorage();

  const [stockItemsStepsModalVisible, setStockItemsStepsModalVisible] =
    useDisclosure(false);

  const items = Form.useWatch('items', form);
  const companyId = Form.useWatch('companyId', form);

  useEffect(() => {
    form.setFieldsValue({ idempotentToken: v4() });
  }, []);

  const { data: employee } = useEmployee(Number(user.employeeId));

  const handleFieldsChange = (changedFields: FieldData[]) => {
    const [field] = changedFields;

    if (field && (field.name as string[])[0] === 'companyId') {
      form.setFieldValue('items', []);
    }

    if (field && (field.name as string[])[0] !== 'idempotentToken') {
      form.setFieldsValue({ idempotentToken: v4() });
    }
  };

  const columnPart = useConfigurationColumnsPart({ internalCode: 'sku' });

  const columns: ColumnsType<StockItem> = [
    columnPart,
    {
      ellipsis: true,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.itemName',
      }),
    },
    {
      ellipsis: true,
      width: 120,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.type',
      }),
      render(row: StockItem) {
        let label = '-';

        if (row.partId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.part',
          });
        } else if (row.consumableId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.consumable',
          });
        }

        return <StatusTag label={label} color="blue" />;
      },
    },
    {
      width: 120,
      dataIndex: 'stock',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.stock',
      }),
      align: 'right',
      render: (_: unknown, record: StockItem, index) => {
        if (!companyId) return '-';

        const companyStock = record.stock?.find(
          (a) => companyId === a.company.id,
        );

        if (!companyStock) return '-';

        return (
          <>
            <Form.Item
              hidden
              name={[index, 'oldQuantity']}
              initialValue={companyStock.stock}
            />

            {quantityFormatter(companyStock?.stock)}
          </>
        );
      },
    },
    {
      width: 185,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.newQuantity',
      }),
      render(row: StockItem, record, index) {
        if (!companyId) return '-';

        const companyStock = record.stock?.find(
          (a) => companyId === a.company.id,
        );

        if (!companyStock) return '-';

        return (
          <>
            {row.partId ? (
              <Form.Item
                hidden
                name={[index, 'partId']}
                initialValue={row.partId}
              >
                <InputDecimal fractions={2} min={0} />
              </Form.Item>
            ) : null}
            {row.consumableId ? (
              <Form.Item
                hidden
                name={[index, 'consumableId']}
                initialValue={row.consumableId}
              >
                <InputDecimal fractions={2} min={0} />
              </Form.Item>
            ) : null}
            <Form.Item
              style={{ margin: 'auto' }}
              name={[index, 'quantity']}
              initialValue={0}
              rules={[
                {
                  type: 'number',
                  min: companyStock.reserved,
                  message: `Quantidade mínima reservada: ${companyStock.reserved}`,
                },
              ]}
            >
              <InputDecimal fractions={2} min={0} />
            </Form.Item>
          </>
        );
      },
    },
    {
      width: 125,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.deltaQuantity',
      }),
      align: 'right',
      render(row: StockItem, _, index: number) {
        return (
          <Form.Item noStyle shouldUpdate>
            {() => {
              const foundStock = row.stock?.find(
                (a) => a.company?.id === companyId,
              );

              if (!foundStock) return 0;

              const qtd = form.getFieldValue([index, 'quantity']);

              return quantityFormatter(qtd - foundStock.stock);
            }}
          </Form.Item>
        );
      },
    },
    {
      width: 60,
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      render: (row) => (
        <DeleteIcon
          onClick={() => {
            const rowKey = getRowKey(row);

            const itemsStock = items.filter((si) => {
              const siRowKey = getRowKey(si);

              return rowKey !== siRowKey;
            });

            form.setFieldValue('items', itemsStock);
          }}
        />
      ),
      fixed: 'right',
      align: 'center',
    },
  ];

  return (
    <Form form={form} onFieldsChange={handleFieldsChange} preserve={false}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24} md={8}>
          <Form.Item
            noStyle
            hidden
            name="items"
            shouldUpdate
            initialValue={[]}
            rules={[
              {
                type: 'array',
                min: 1,
                message: intl.formatMessage({
                  id: 'stockCorrections.create.errors.atLeastOneItem',
                }),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item noStyle hidden name="idempotentToken" initialValue="">
            <Input />
          </Form.Item>

          <FloatingFormItem
            label={intl.formatMessage({ id: 'stockCorrections.domain.date' })}
          >
            <DatePicker
              disabled
              value={moment()}
              format="DD/MM/YYYY, HH:mm"
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'stockCorrections.domain.employee',
            })}
          >
            <Input disabled value={employee?.name} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8}>
          <FloatingFormItem
            name="companyId"
            label={intl.formatMessage({
              id: 'stockCorrections.domain.company',
            })}
            rules={[defaultEmptyFieldError]}
          >
            <CompaniesInfiniteSelectField />
          </FloatingFormItem>
        </Col>
      </Row>

      <Row align="middle" justify="center" gutter={[16, 16]}>
        <Col flex={1}>
          <Title level={3} style={{ margin: 0 }}>
            <FormattedMessage id="stockCorrections.domain.items" />
          </Title>
        </Col>

        <Col>
          <Button
            block
            type="primary"
            disabled={!companyId}
            onClick={setStockItemsStepsModalVisible.open}
          >
            Adicionar
          </Button>
        </Col>

        <Col span={24}>
          <Form.Item noStyle shouldUpdate>
            {() => (
              <div style={{ marginBottom: '16px' }}>
                <Text type="danger">{form.getFieldError(['items'])}</Text>
              </div>
            )}
          </Form.Item>

          <Form.List name="items">
            {() => (
              <Table
                size="middle"
                columns={columns}
                dataSource={form.getFieldValue('items')}
                scroll={{ x: getTableScrollX(columns) }}
                rowKey={(row) => getRowKey(row)}
                pagination={false}
              />
            )}
          </Form.List>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="observation"
            label={intl.formatMessage({
              id: 'stockCorrections.domain.observation',
            })}
          >
            <Input.TextArea rows={4} />
          </FloatingFormItem>
        </Col>
      </Row>

      <StockCorrectionCreateModal
        form={form}
        selectedItems={items}
        companyId={companyId}
        open={stockItemsStepsModalVisible}
        onCancel={setStockItemsStepsModalVisible.close}
      />
    </Form>
  );
};
